import React, { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { formatPLANumber } from "./PLAFormatting";
import {
   collectPlanitElements,
   collectPlanitVariables,
   compareFindingsToPLA,
   diffPlanitRuns,
   loadPlanitCheckHistory,
   savePlanitCheckRun,
   PlanitDocumentKind,
   PLALLMCheckApplyChange,
   PLALLMCheckStatus,
   PLALLMCheckVersionChange,
   PLALLMCompareRow,
   type PlanitExtractResult,
} from "./PLALLMCheck";
import { extractPlanitDocument } from "./PLALLMCheckApi";
import { isPLANumberValueType, PLAElementRecord, PLAProject, PLARelationRecord, PLAValueType } from "./PLAState";
import "./PLALLMCheck.css";

type ViewTab = "compare" | "changes";

export function PLALLMCheckDialog(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   readOnly: boolean;
   caller?: { userRef: string; userName: string; organizationId: string };
   onClose: () => void;
   onApplyValues: (changes: PLALLMCheckApplyChange[]) => Promise<boolean>;
}) {
   const [kind, setKind] = useState<PlanitDocumentKind>("lokalplan");
   const [file, setFile] = useState<File | undefined>();
   const [running, setRunning] = useState(false);
   const [error, setError] = useState("");
   const [tab, setTab] = useState<ViewTab>("compare");
   const [result, setResult] = useState<PlanitExtractResult | undefined>(() => loadPlanitCheckHistory(props.project.id, "lokalplan").current?.result);
   const [filename, setFilename] = useState(() => loadPlanitCheckHistory(props.project.id, "lokalplan").current?.filename || "");
   const [previous, setPrevious] = useState(() => loadPlanitCheckHistory(props.project.id, "lokalplan").previous);
   const [selectedIds, setSelectedIds] = useState<Record<string, boolean>>({});

   const rows = useMemo(
      () => result ? compareFindingsToPLA(props.project, props.elements, props.relations, result) : [],
      [props.project, props.elements, props.relations, result]
   );
   const versionRows = useMemo(() => result ? diffPlanitRuns(previous?.result, result) : [], [previous, result]);
   const appliableRows = rows.filter((row) => row.appliable && row.elementId && row.attributeId && typeof row.documentValue === "number");

   const runCheck = async () => {
      if (!file) return;
      setRunning(true);
      setError("");
      try {
         const extracted = await extractPlanitDocument({
            kind,
            file,
            variables: collectPlanitVariables(props.project),
            elements: collectPlanitElements(props.project, props.elements, props.relations),
            caller: props.caller,
         });
         const history = savePlanitCheckRun(props.project.id, kind, {
            timestamp: new Date().toISOString(),
            filename: file.name,
            kind,
            result: extracted,
         });
         setResult(extracted);
         setFilename(file.name);
         setPrevious(history.previous);
         setSelectedIds({});
         setTab("compare");
      } catch (caught: any) {
         setError(caught?.message || String(caught));
      } finally {
         setRunning(false);
      }
   };

   const applySelected = async () => {
      const changes: PLALLMCheckApplyChange[] = appliableRows
         .filter((row) => selectedIds[row.id])
         .map((row) => {
               const valueType = resolveValueType(props.project, row, props.elements);
            return {
               elementId: row.elementId!,
               attributeId: row.attributeId!,
               value: row.documentValue as number,
               valueType,
            };
         });
      if (!changes.length) return;
      const saved = await props.onApplyValues(changes);
      if (saved) setSelectedIds({});
   };

   const toggleAllAppliable = (checked: boolean) => {
      const next: Record<string, boolean> = { ...selectedIds };
      appliableRows.forEach((row) => { next[row.id] = checked; });
      setSelectedIds(next);
   };

   const switchKind = (nextKind: PlanitDocumentKind) => {
      setKind(nextKind);
      const history = loadPlanitCheckHistory(props.project.id, nextKind);
      setResult(history.current?.result);
      setFilename(history.current?.filename || "");
      setPrevious(history.previous);
      setSelectedIds({});
      setFile(undefined);
      setError("");
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("LLM check")}
         PosDefault={{ width: "960px", height: "640px", left: "50%", transform: "translateX(-50%)", top: "70px" }}
         onClose={props.onClose}
      >
         <ADModalBody>
            <div className="pla-llm-check">
               <div className="pla-llm-check__toolbar">
                  <label>
                     <input type="radio" name="pla-llm-kind" checked={kind === "lokalplan"} onChange={() => switchKind("lokalplan")} />
                     {Localization.getText("LLM check lokalplan")}
                  </label>
                  <label>
                     <input type="radio" name="pla-llm-kind" checked={kind === "arealskema"} onChange={() => switchKind("arealskema")} />
                     {Localization.getText("LLM check arealskema")}
                  </label>
                  <input
                     className="pla-llm-check__file"
                     type="file"
                     accept={kind === "lokalplan" ? "application/pdf,.pdf" : undefined}
                     onChange={(event) => setFile(event.target.files?.[0] || undefined)}
                  />
                  <GlassButton className="pla-btn pla-btn--solid" disabled={!file || running} onClick={runCheck}>
                     {running ? <span className="pla-hero-cta__copy"><Spinner animation="border" size="sm" />{Localization.getText("Run LLM check")}</span> : Localization.getText("Run LLM check")}
                  </GlassButton>
               </div>
               {running ? <p className="pla-llm-check__hint">{Localization.getText("Checking document with AI. This can take several minutes.")}</p> : null}
               {filename ? <p className="pla-llm-check__hint">{filename}{result?.model ? ` · ${result.model}` : ""}{result?.pages ? ` · ${result.pages}` : ""}</p> : null}
               {error ? <p className="pla-llm-check__error">{error}</p> : null}
               {result?.warnings?.length ? <p className="pla-llm-check__warnings">{result.warnings.join(" ")}</p> : null}

               <div className="pla-llm-check__tabs">
                  <GlassButton className="pla-btn pla-btn--ghost" onClick={() => setTab("compare")}>{Localization.getText("Compare with PLA")}</GlassButton>
                  <GlassButton className="pla-btn pla-btn--ghost" onClick={() => setTab("changes")}>{Localization.getText("Changes since previous version")}</GlassButton>
               </div>

               {tab === "compare" ? (
                  rows.length ? (
                     <div className="pla-llm-check__table-wrap">
                        <table>
                           <thead>
                              <tr>
                                 {!props.readOnly ? (
                                    <th>
                                       <input
                                          type="checkbox"
                                          checked={appliableRows.length > 0 && appliableRows.every((row) => selectedIds[row.id])}
                                          onChange={(event) => toggleAllAppliable(event.target.checked)}
                                          aria-label={Localization.getText("Apply selected values")}
                                       />
                                    </th>
                                 ) : null}
                                 <th>{Localization.getText("Variable")}</th>
                                 <th>{Localization.getText("Element")}</th>
                                 <th>{Localization.getText("PLA value")}</th>
                                 <th>{Localization.getText("Document value")}</th>
                                 <th>{Localization.getText("Status")}</th>
                                 <th>{Localization.getText("Quote")}</th>
                                 <th>{Localization.getText("Page")}</th>
                              </tr>
                           </thead>
                           <tbody>
                              {rows.map((row) => (
                                 <tr key={row.id}>
                                    {!props.readOnly ? (
                                       <td>
                                          {row.appliable ? (
                                             <input
                                                type="checkbox"
                                                checked={Boolean(selectedIds[row.id])}
                                                onChange={(event) => setSelectedIds((current) => ({ ...current, [row.id]: event.target.checked }))}
                                             />
                                          ) : null}
                                       </td>
                                    ) : null}
                                    <td>{row.variableName}</td>
                                    <td>{row.elementLabel}</td>
                                    <td>{formatCellValue(row.plaValue)}</td>
                                    <td>{formatCellValue(row.documentValue)}{row.unit ? ` ${row.unit}` : ""}</td>
                                    <td><span className={`pla-llm-check__status pla-llm-check__status--${row.status}`}>{statusText(row.status)}</span></td>
                                    <td className="pla-llm-check__quote">{row.quote}{row.paragraph ? ` (${row.paragraph})` : ""}</td>
                                    <td>{row.page ?? ""}</td>
                                 </tr>
                              ))}
                           </tbody>
                        </table>
                     </div>
                  ) : <p className="pla-llm-check__hint">{Localization.getText("LLM check no findings")}</p>
               ) : versionRows.length ? (
                  <div className="pla-llm-check__table-wrap">
                     <table>
                        <thead>
                           <tr>
                              <th>{Localization.getText("Status")}</th>
                              <th>{Localization.getText("Variable")}</th>
                              <th>{Localization.getText("Element")}</th>
                              <th>{Localization.getText("Previous file")}</th>
                              <th>{Localization.getText("Current file")}</th>
                              <th>{Localization.getText("Quote")}</th>
                              <th>{Localization.getText("Page")}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {versionRows.map((row) => (
                              <tr key={row.id}>
                                 <td><span className={`pla-llm-check__status pla-llm-check__status--${row.change}`}>{changeText(row.change)}</span></td>
                                 <td>{row.variableId}</td>
                                 <td>{row.elementLabel}</td>
                                 <td>{formatCellValue(row.previousValue)}</td>
                                 <td>{formatCellValue(row.currentValue)}</td>
                                 <td className="pla-llm-check__quote">{row.quote}</td>
                                 <td>{row.page ?? ""}</td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  </div>
               ) : <p className="pla-llm-check__hint">{Localization.getText("No previous run")}</p>}
            </div>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow adjust="end">
               {!props.readOnly && tab === "compare" ? (
                  <GlassButton disabled={!appliableRows.some((row) => selectedIds[row.id])} onClick={applySelected}>
                     {Localization.getText("Apply selected values")}
                  </GlassButton>
               ) : null}
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}

function formatCellValue(value: number | string | null): string {
   if (value === null || value === undefined || value === "") return "";
   if (typeof value === "number") return formatPLANumber(value);
   return String(value);
}

function statusText(status: PLALLMCheckStatus): string {
   if (status === "ok") return Localization.getText("OK");
   if (status === "mismatch") return Localization.getText("Mismatch");
   if (status === "missing_in_pla") return Localization.getText("Missing in PLA");
   return Localization.getText("Missing in document");
}

function changeText(change: PLALLMCheckVersionChange): string {
   if (change === "added") return Localization.getText("Added");
   if (change === "removed") return Localization.getText("Removed");
   return Localization.getText("Changed");
}

function resolveValueType(project: PLAProject, row: PLALLMCompareRow, elements: PLAElementRecord[]): PLAValueType {
   const element = row.elementId ? elements.find((item) => item.id === row.elementId) : undefined;
   if (element && row.attributeId) {
      const valueType = project.elementTypes[element.elementType]?.attributes?.[row.attributeId]?.valueType;
      if (valueType) return valueType;
   }
   for (const elementType of Object.values(project.elementTypes || {})) {
      const valueType = elementType.attributes?.[row.attributeId || ""]?.valueType;
      if (isPLANumberValueType(valueType)) return valueType;
      if (valueType) return valueType;
   }
   return "number";
}
